import React, { useState, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { AppProvider, useApp } from './context/AppContext'
import { authedFetch } from './lib/api'

import Landing from './pages/Landing/Landing'
import Register from './pages/Auth/Register'
import Login from './pages/Auth/Login'
import Onboarding from './pages/Onboarding/Onboarding'
import Results from './pages/Results/Results'
import Payment from './pages/Payment/Payment'
import Dashboard from './pages/Dashboard/Dashboard'
import Slot from './pages/Slot/Slot'
import Calendar from './pages/Calendar/Calendar'
import Education from './pages/Education/Education'
import Journal from './pages/Journal/Journal'
import Profile from './pages/Profile/Profile'

import Layout from './components/Layout/Layout'
import CookieConsent from './components/CookieConsent/CookieConsent'
import './styles/global.css'

function AppRoutes() {
  const { user, loading, hasOnboarding, hasPaid } = useApp()
  const routeLocation = useLocation()

  // Trimite page_view la GA4 la fiecare schimbare de ruta (SPA)
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: routeLocation.pathname + routeLocation.search,
      })
    }
  }, [routeLocation])

  if (loading) return <div className="loading-screen"><span>Se incarca...</span></div>

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/register" element={!user ? <Register /> : hasOnboarding ? <Navigate to="/dashboard" /> : <Navigate to="/onboarding" />} />
      <Route path="/login" element={!user ? <Login /> : hasOnboarding ? <Navigate to="/dashboard" /> : <Navigate to="/onboarding" />} />
      <Route path="/educatie" element={user ? <Layout><Education /></Layout> : <Navigate to="/register" />} />
      <Route path="/onboarding" element={
        user ? hasOnboarding ? <Navigate to="/dashboard" /> : <Onboarding />
        : <Navigate to="/register" />
      } />
      <Route path="/rezultate" element={
        user ? !hasOnboarding ? <Navigate to="/onboarding" />
          : <Results />
        : <Navigate to="/register" />
      } />
      <Route path="/payment" element={
        user ? !hasOnboarding ? <Navigate to="/onboarding" />
          : hasPaid ? <Navigate to="/slot" /> : <Payment />
        : <Navigate to="/register" />
      } />
      <Route path="/dashboard" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/slot" element={<PaidRoute><Layout><Slot /></Layout></PaidRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><Layout><Calendar /></Layout></ProtectedRoute>} />
      <Route path="/jurnal" element={<ProtectedRoute><Layout><Journal /></Layout></ProtectedRoute>} />
      <Route path="/profil" element={<ProtectedRoute><Layout><Profile /></Layout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  )
}

// Ruta accesibila dupa cont + onboarding (restul aplicatiei e gratuit)
function ProtectedRoute({ children }) {
  const { user, hasOnboarding } = useApp()
  const location = useLocation()
  if (!user) return <Navigate to="/register" state={{ from: location }} />
  if (!hasOnboarding) return <Navigate to="/onboarding" />
  return children
}

// Ruta care necesita abonament activ (doar simulatorul). Altfel -> /payment
function PaidRoute({ children }) {
  const { user, hasOnboarding, hasPaid, loadUserData } = useApp()
  const location = useLocation()
  const justPaid = new URLSearchParams(location.search).get('checkout') === 'success'
  const [confirming, setConfirming] = useState(justPaid && !hasPaid)

  // Dupa intoarcerea de la Stripe, verificam activ abonamentul (sync direct cu Stripe)
  // si reincarcam datele pana cand has_paid devine true (max ~24s).
  useEffect(() => {
    if (!justPaid || hasPaid || !user?.id) { setConfirming(false); return }
    setConfirming(true)
    let tries = 0
    const interval = setInterval(async () => {
      tries++
      try { await authedFetch('/sync-subscription', { method: 'POST' }) } catch (e) { /* retry */ }
      await loadUserData(user.id)
      if (tries >= 8) { clearInterval(interval); setConfirming(false) }
    }, 3000)
    return () => clearInterval(interval)
  }, [justPaid, hasPaid, user, loadUserData])

  if (!user) return <Navigate to="/register" state={{ from: location }} />
  if (!hasOnboarding) return <Navigate to="/onboarding" />
  if (hasPaid) return children
  if (confirming) return <div className="loading-screen"><span>Confirmam plata...</span></div>
  return <Navigate to="/payment" />
}

export default function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <AppRoutes />
        <CookieConsent />
      </AppProvider>
    </BrowserRouter>
  )
}
